import { Amplify, Auth } from "aws-amplify";
import { SignUp } from "kh-design-system/components";
import React, { useEffect, useState } from "react";
import { generateRandomPass } from "../../reusable/common";
import {
	amplifyAuthErrors,
	authentication,
	authenticationModuleNames,
	errors
} from "../../utils/constants";
import { useEvent } from "../../contexts/EventContext";
import { getSubRoute } from "../../utils/core";
import { authConfig, defaultAwsConfig, userPoolWebClientId } from "../../config/aws-exports";

const SignUpWrapper = ({
	emailValue,
	setEmailValue,
	setSwitchComponent,
	setIsSignIn,
	setIsSignUp,
	setRequestOtpEmail,
	isError,
	setIsError,
	toasterMessage,
	setToasterMessage,
	notificationPopup,
	setNotificationPopup,
	...props
}: {
	emailValue: string;
	setEmailValue: React.Dispatch<React.SetStateAction<string>>;
	setSwitchComponent: React.Dispatch<React.SetStateAction<string>>;
	setIsSignIn: React.Dispatch<React.SetStateAction<boolean>>;
	setIsSignUp: React.Dispatch<React.SetStateAction<boolean>>;
	setRequestOtpEmail: React.Dispatch<React.SetStateAction<string>>;
	isError: boolean;
	setIsError: React.Dispatch<React.SetStateAction<boolean>>;
	toasterMessage: string;
	setToasterMessage: React.Dispatch<React.SetStateAction<string>>;
	notificationPopup: boolean;
	setNotificationPopup: React.Dispatch<React.SetStateAction<boolean>>;
	[key: string]: any;
}) => {
	const { eventDetails } = useEvent();
	const primaryColor = eventDetails?.gallery_brand_color;
	const fontFamily = eventDetails?.gallery_font;
	const [loading, setLoading] = useState(false);

	const signInWithGoogle = async () => {
		try {
			await Auth.federatedSignIn({ provider: "Google" });
			localStorage.setItem("path", getSubRoute());
		} catch (error) {
			console.error("Error during Google Sign-In:", error);
		}
	};
	const signInWithAmazon = async () => {
		try {
			await Auth.federatedSignIn({ provider: "Amazon" });
			localStorage.setItem("path", getSubRoute());
		} catch (error) {
			console.error("Error during Amazon Sign-In:", error);
		}
	};

	const signInWithFacebook = async () => {
		try {
			await Auth.federatedSignIn({ provider: "Facebook" });
			localStorage.setItem("path", getSubRoute());
		} catch (error) {
			console.error("Error during Facebook Sign-In:", error);
		}
	};

	const resendSignUp = async (message: string, email: string) => {
		try {
			const response = await Auth.resendSignUp(email?.toLowerCase() || "");
			if (response) {
				setToasterMessage(message);
				setNotificationPopup(true);
				setIsSignIn(false);
				setIsSignUp(true);
				setRequestOtpEmail(email);
				setTimeout(() => {
					setSwitchComponent(authenticationModuleNames.OTP_VERIFICATION);
				}, 700);
			}
		} catch (err: any) {
			setIsError(true);
			setToasterMessage(err.message || errors.SOMETHING_WENT_WRONG);
			setNotificationPopup(true);
		}
	};

	const handleSignUp = async (isFormValid: boolean, email: string, password: string) => {
		if (!isFormValid) return;

		setLoading(true);
		try {
			await Auth.signIn(email.toLowerCase(), generateRandomPass());
		} catch (err: any) {
			const error = err.message || errors.SOMETHING_WENT_WRONG;
			if (error === amplifyAuthErrors.INCORRECT_USERNAME_PASSWORD) {
				setToasterMessage(authentication.error.ALREADY_REGISTERED);
				setIsError(true);
				setNotificationPopup(true);
				setEmailValue(email);
			} else if (error === amplifyAuthErrors.USER_NOT_EXIST) {
				try {
					const signUpDetails = {
						username: email.toLowerCase(),
						password: password,
						attributes: {
							name: email.split("@")[0].toLowerCase(),
							email: email.toLowerCase(),
							"custom:signUpEventId": localStorage.getItem("eventId") || ""
						}
					};
					const response = await Auth.signUp(signUpDetails);
					if (response) {
						sessionStorage.setItem("email", signUpDetails.username);
						sessionStorage.setItem("password", signUpDetails.password);
						setToasterMessage(
							authentication.success.VERIFICATION_CODE_SENT_SUCCESS_EMAIL
						);
						setNotificationPopup(true);
						setIsSignIn(false);
						setIsSignUp(true);
						setRequestOtpEmail(email);
						setTimeout(() => {
							setSwitchComponent(authenticationModuleNames.OTP_VERIFICATION);
						}, 700);
					}
				} catch (err: any) {
					handleSignUpError(err);
				}
			} else if (error === amplifyAuthErrors.USER_NOT_CONFIRMED) {
				await resendSignUp(authentication.success.CODE_SENT_SUCCESS, email);
			} else {
				handleSignUpError(err);
			}
		} finally {
			setLoading(false);
		}
	};

	const handleSignUpError = (err: any) => {
		let error = err.message || errors.SOMETHING_WENT_WRONG;
		if (error === amplifyAuthErrors.ACCOUNT_ALREADY_EXIST) {
			error = authentication.error.EMAIL_ALREADY_EXISTS;
		}
		if (error.includes("PreSignUp failed with error")) {
			error = error.replace("PreSignUp failed with error ", "");
		}
		setIsError(true);
		setToasterMessage(error);
		setNotificationPopup(true);
	};

	const requestOtpSignUp = async (email: string) => {
		if (!email) {
			setIsError(true);
			setToasterMessage(authentication.error.REQUIRED_EMAIL);
			setNotificationPopup(true);
			return;
		}
		setLoading(true);
		if (true) {
			try {
				await Auth.signIn(email?.toLowerCase() || "", generateRandomPass());
			} catch (err: any) {
				const error = err.message || errors.SOMETHING_WENT_WRONG;
				if (error === amplifyAuthErrors.INCORRECT_USERNAME_PASSWORD) {
					setIsError(true);
					setToasterMessage(authentication.error.ALREADY_REGISTERED);
					setNotificationPopup(true);
					setLoading(false);
					setEmailValue(email);
				} else if (error === amplifyAuthErrors.USER_NOT_EXIST) {
					try {
						const signupDetails: any = {
							username: email?.toLowerCase(),
							password: generateRandomPass(),
							attributes: {
								name: email
									?.toLowerCase()
									?.substring(0, email?.toLowerCase()?.indexOf("@")),
								email: email?.toLowerCase(),
								"custom:signUpEventId": localStorage.getItem("eventId") || ""
							}
						};

						const response = await Auth.signUp(signupDetails);
						if (response) {
							sessionStorage.setItem("email", signupDetails?.username);
							sessionStorage.setItem("password", signupDetails?.password);
							setToasterMessage(authentication.success.CODE_SENT_SUCCESS);
							setNotificationPopup(true);
							setLoading(false);
							setEmailValue(email);
							setRequestOtpEmail(email);
							setTimeout(() => {
								setSwitchComponent(authenticationModuleNames.OTP_VERIFICATION);
							}, 700);
						}
					} catch (error: any) {
						setLoading(false);
						setToasterMessage(error.message);
						setNotificationPopup(true);
					}
				} else if (error === amplifyAuthErrors.USER_NOT_CONFIRMED) {
					await resendSignUp(authentication.success.CODE_SENT_SUCCESS, email);
					setLoading(false);
				} else {
					setLoading(false);
					setToasterMessage(error);
					setNotificationPopup(true);
				}
			}
		}
	};

	useEffect(() => {
		Amplify.configure({
			...defaultAwsConfig,
			Auth: { ...defaultAwsConfig.Auth, userPoolWebClientId: userPoolWebClientId }
		});
		Auth.configure({
			...(authConfig as any),
			aws_user_pools_web_client_id: userPoolWebClientId
		});
	}, []);

	return (
		<>
			<SignUp
				emailValue={emailValue}
				handleSignUp={handleSignUp}
				requestOtp={requestOtpSignUp}
				onClickSignIn={() => {
					setIsSignUp(false);
					setIsSignIn(true);
					setSwitchComponent(authenticationModuleNames.SIGN_IN);
				}}
				isLogoVisible={false}
				signInWithGoogle={signInWithGoogle}
				signInWithAmazon={signInWithAmazon}
				signInWithFacebook={signInWithFacebook}
				loading={loading}
				primaryColor={primaryColor}
				fontFamily={fontFamily}
			/>
		</>
	);
};

export default SignUpWrapper;
